/**
 * SocialInbox — unified inbox for comments and DMs from connected platforms.
 * Two-column layout: left platform nav, right message feed.
 * Auto-selects first platform + account on load; persists selection to localStorage.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MessageSquare,
  Send,
  Mail,
  Facebook,
  Instagram,
  Youtube,
  Music2,
  RefreshCw,
  Reply,
  ChevronDown,
  ChevronUp,
  Inbox,
  User,
  Search,
  X,
  Sparkles,
  Loader2,
} from "lucide-react";

// ── Types ──

type SocialPlatform = "facebook" | "instagram" | "youtube" | "tiktok";
type MessageType = "comment" | "dm";

interface SocialMessage {
  id: string;
  platform: SocialPlatform;
  type: MessageType;
  authorName: string;
  authorAvatarUrl?: string;
  content: string;
  timestamp: string;
  postId?: string;
  postCaption?: string;
  parentId?: string;
}

interface SocialConversation {
  id: string;
  platform: SocialPlatform;
  participantName: string;
  participantAvatarUrl?: string;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  messages: SocialMessage[];
}

interface ConnectedPlatform {
  platform: SocialPlatform;
  connected: boolean;
  supportsDm: boolean;
}

interface AccountInfo {
  id: string;
  name: string;
  platform: SocialPlatform;
  type: string;
}

interface InboxData {
  comments: SocialMessage[];
  conversations: SocialConversation[];
}

// ── Constants ──

const PLATFORM_CONFIG: Record<
  SocialPlatform,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    textColor: string;
  }
> = {
  facebook: {
    label: "Facebook",
    icon: <Facebook size={14} />,
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    textColor: "text-blue-400",
  },
  instagram: {
    label: "Instagram",
    icon: <Instagram size={14} />,
    color: "text-pink-400 bg-pink-500/10 border-pink-500/20",
    textColor: "text-pink-400",
  },
  youtube: {
    label: "YouTube",
    icon: <Youtube size={14} />,
    color: "text-red-400 bg-red-500/10 border-red-500/20",
    textColor: "text-red-400",
  },
  tiktok: {
    label: "TikTok",
    icon: <Music2 size={14} />,
    color: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
    textColor: "text-cyan-400",
  },
};

const TYPE_CONFIG: Record<MessageType, { icon: React.ReactNode }> = {
  comment: { icon: <MessageSquare size={12} /> },
  dm: { icon: <Mail size={12} /> },
};

// ── Persistence ──

const STORAGE_KEY = "social-inbox-v1";

interface PersistedState {
  platform: SocialPlatform | null;
  accountsByPlatform: Partial<Record<SocialPlatform, string>>;
}

function loadSavedState(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PersistedState;
      return {
        platform: parsed.platform ?? null,
        accountsByPlatform: parsed.accountsByPlatform ?? {},
      };
    }
  } catch {}
  return { platform: null, accountsByPlatform: {} };
}

function saveState(platform: SocialPlatform, accountId: string) {
  try {
    const current = loadSavedState();
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        platform,
        accountsByPlatform: { ...current.accountsByPlatform, [platform]: accountId },
      })
    );
  } catch {}
}

// ── Helpers ──

function formatTimestamp(ts: string, justNow: string, locale: string): string {
  const date = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return justNow;
  if (diffMin < 60) return `${diffMin} min`;

  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h`;

  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d`;

  return date.toLocaleDateString(locale, { day: "numeric", month: "short" });
}

// ── Sub-components ──

function PlatformBadge({ platform }: { platform: SocialPlatform }) {
  const config = PLATFORM_CONFIG[platform];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border ${config.color}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

function EmptyState() {
  const { t } = useTranslation("socialInbox");
  return (
    <div className="flex flex-col items-center justify-center py-20 text-white/30">
      <Inbox size={48} className="mb-4" />
      <p className="text-sm font-medium">{t("emptyState.heading")}</p>
      <p className="text-xs mt-1">{t("emptyState.subtext")}</p>
    </div>
  );
}

function CommentCard({
  message,
  onReply,
  isReplying,
}: {
  message: SocialMessage;
  onReply: (commentId: string, platform: SocialPlatform, text: string) => void;
  isReplying: boolean;
}) {
  const { t, i18n } = useTranslation("socialInbox");
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [isLoadingAi, setIsLoadingAi] = useState(false);

  const handleSubmitReply = () => {
    if (!replyText.trim()) return;
    onReply(message.id, message.platform, replyText);
    setReplyText("");
    setShowReplyInput(false);
    setAiSuggestions([]);
  };

  const handleGenerateAi = async () => {
    setIsLoadingAi(true);
    setAiSuggestions([]);
    try {
      const res = await apiRequest("POST", "/api/social-inbox/ai-reply", {
        commentText: message.content,
        authorName: message.authorName,
        postCaption: message.postCaption,
        platform: message.platform,
      });
      setAiSuggestions(res.suggestions ?? []);
    } catch {
      setAiSuggestions([]);
    } finally {
      setIsLoadingAi(false);
    }
  };

  const handlePickSuggestion = (text: string) => {
    setReplyText(text);
    setAiSuggestions([]);
  };

  return (
    <div className="p-4 border border-white/[0.06] rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/[0.08] flex items-center justify-center">
          {message.authorAvatarUrl ? (
            <img src={message.authorAvatarUrl} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <User size={14} className="text-white/40" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-white">{message.authorName}</span>
            <PlatformBadge platform={message.platform} />
            <span className="text-[10px] text-white/30 ml-auto">
              {formatTimestamp(message.timestamp, t("justNow"), i18n.language)}
            </span>
          </div>

          {message.postCaption && (
            <p className="text-[11px] text-white/25 mb-1 truncate">Re: {message.postCaption}</p>
          )}

          <p className="text-sm text-white/70 leading-relaxed">{message.content}</p>

          <div className="mt-2">
            {!showReplyInput ? (
              <button
                onClick={() => setShowReplyInput(true)}
                className="flex items-center gap-1.5 text-xs text-white/30 hover:text-white/60 transition-colors"
              >
                <Reply size={12} />
                {t("reply")}
              </button>
            ) : (
              <div className="space-y-2 mt-1">
                {aiSuggestions.length > 0 && (
                  <div className="space-y-1.5">
                    <span className="text-[10px] text-white/30 font-medium uppercase tracking-wider">
                      {t("aiSuggestions")}
                    </span>
                    {aiSuggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => handlePickSuggestion(s)}
                        className="w-full text-left px-3 py-2 text-xs text-white/60 bg-white/[0.03] border border-white/[0.08] rounded-lg hover:bg-white/[0.06] hover:text-white/80 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={t("replyPlaceholder")}
                    className="h-8 text-xs bg-white/[0.04] border-white/[0.08]"
                    onKeyDown={(e) => e.key === "Enter" && handleSubmitReply()}
                    autoFocus
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleGenerateAi}
                    disabled={isLoadingAi}
                    className="h-8 px-2.5 text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                    title={t("aiButtonTitle")}
                  >
                    {isLoadingAi ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Sparkles size={12} />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSubmitReply}
                    disabled={!replyText.trim() || isReplying}
                    className="h-8 px-3 bg-[#ef3e29] hover:bg-[#d63520] text-xs"
                  >
                    <Send size={12} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowReplyInput(false);
                      setReplyText("");
                      setAiSuggestions([]);
                    }}
                    className="h-8 px-2 text-xs text-white/40"
                  >
                    ✕
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConversationCard({
  conversation,
  onSendDm,
  isSending,
}: {
  conversation: SocialConversation;
  onSendDm: (recipientId: string, platform: SocialPlatform, text: string) => void;
  isSending: boolean;
}) {
  const { t, i18n } = useTranslation("socialInbox");
  const [expanded, setExpanded] = useState(false);
  const [dmText, setDmText] = useState("");

  const handleSendDm = () => {
    if (!dmText.trim()) return;
    onSendDm(conversation.id, conversation.platform, dmText);
    setDmText("");
  };

  return (
    <div className="border border-white/[0.06] rounded-xl bg-white/[0.02] overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 hover:bg-white/[0.03] transition-colors text-left"
      >
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/[0.08] flex items-center justify-center">
          <User size={14} className="text-white/40" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">{conversation.participantName}</span>
            <PlatformBadge platform={conversation.platform} />
            {conversation.unreadCount > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-[#ef3e29] text-white rounded-full">
                {conversation.unreadCount}
              </span>
            )}
          </div>
          <p className="text-xs text-white/40 truncate mt-0.5">{conversation.lastMessage}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-white/25">
            {formatTimestamp(conversation.lastMessageTime, t("justNow"), i18n.language)}
          </span>
          {expanded ? (
            <ChevronUp size={14} className="text-white/30" />
          ) : (
            <ChevronDown size={14} className="text-white/30" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-white/[0.06]">
          <div className="max-h-60 overflow-y-auto p-3 space-y-2">
            {conversation.messages.map((msg) => (
              <div key={msg.id} className="flex items-start gap-2">
                <span className="text-xs font-medium text-white/50 min-w-[60px]">
                  {msg.authorName}
                </span>
                <p className="text-xs text-white/70 flex-1">{msg.content}</p>
                <span className="text-[10px] text-white/20 flex-shrink-0">
                  {formatTimestamp(msg.timestamp, t("justNow"), i18n.language)}
                </span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 p-3 border-t border-white/[0.06]">
            <Input
              value={dmText}
              onChange={(e) => setDmText(e.target.value)}
              placeholder={t("messagePlaceholder")}
              className="h-8 text-xs bg-white/[0.04] border-white/[0.08]"
              onKeyDown={(e) => e.key === "Enter" && handleSendDm()}
            />
            <Button
              size="sm"
              onClick={handleSendDm}
              disabled={!dmText.trim() || isSending}
              className="h-8 px-3 bg-[#ef3e29] hover:bg-[#d63520] text-xs"
            >
              <Send size={12} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Account Dropdown ──

function AccountDropdown({
  accounts,
  selectedAccount,
  accountsLoading,
  onSelect,
}: {
  accounts: AccountInfo[];
  selectedAccount: AccountInfo | null;
  accountsLoading: boolean;
  onSelect: (a: AccountInfo) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { t } = useTranslation("socialInbox");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setSearch("");
    }
  }, [open]);

  const filtered = search
    ? accounts.filter((a) => a.name.toLowerCase().includes(search.toLowerCase()))
    : accounts;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-lg border transition-all min-w-[120px] max-w-[220px] ${
          selectedAccount
            ? "bg-white/[0.08] text-white border-white/[0.12]"
            : accountsLoading
              ? "text-white/30 border-white/[0.06]"
              : "text-white/50 border-white/[0.10] hover:border-white/[0.18] hover:text-white/70"
        }`}
      >
        <User size={10} className="flex-shrink-0" />
        <span className="truncate flex-1 text-left">
          {accountsLoading
            ? t("accountDropdown.loading")
            : selectedAccount
              ? selectedAccount.name
              : t("accountDropdown.placeholder")}
        </span>
        <ChevronDown
          size={10}
          className={`flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-[#0f1117] border border-white/[0.10] rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.06]">
            <Search size={12} className="text-white/30 flex-shrink-0" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("accountDropdown.searchPlaceholder")}
              className="flex-1 bg-transparent text-xs text-white placeholder:text-white/25 outline-none"
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-white/30 hover:text-white/60">
                <X size={10} />
              </button>
            )}
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <p className="px-3 py-4 text-xs text-white/20 text-center">
                {search ? t("accountDropdown.noResults") : t("accountDropdown.noAccounts")}
              </p>
            )}
            {filtered.map((acc) => (
              <button
                key={acc.id}
                onClick={() => {
                  onSelect(acc);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs transition-colors ${
                  selectedAccount?.id === acc.id
                    ? "bg-white/[0.08] text-white"
                    : "text-white/60 hover:bg-white/[0.04] hover:text-white/80"
                }`}
              >
                <User size={11} className="flex-shrink-0 text-white/30" />
                <span className="truncate flex-1">{acc.name}</span>
                <span className="text-[9px] text-white/20 flex-shrink-0">({acc.type})</span>
              </button>
            ))}
          </div>

          <div className="px-3 py-1.5 border-t border-white/[0.06] text-[10px] text-white/20">
            {t("accountDropdown.accountCount", {
              filtered: filtered.length,
              total: accounts.length,
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ──

export default function SocialInbox() {
  const { t } = useTranslation("socialInbox");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const [selectedPlatform, setSelectedPlatform] = useState<SocialPlatform | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<AccountInfo | null>(null);
  const [selectedType, setSelectedType] = useState<MessageType | null>(null);
  const [platformInitialized, setPlatformInitialized] = useState(false);

  // Fetch connected platforms
  const { data: platformsData } = useQuery<{ platforms: ConnectedPlatform[] }>({
    queryKey: ["/api/social-inbox/connected-platforms"],
    queryFn: () => apiRequest("GET", "/api/social-inbox/connected-platforms"),
  });

  // useMemo, bo `?? []` tworzy nową tablicę przy każdym renderze i restartuje efekt
  // auto-wyboru platformy niżej (react-hooks/exhaustive-deps).
  const connectedPlatforms = useMemo(() => platformsData?.platforms ?? [], [platformsData]);

  // Fetch accounts for selected platform
  const { data: accountsData, isLoading: accountsLoading } = useQuery<{ accounts: AccountInfo[] }>({
    queryKey: ["/api/social-inbox/accounts", selectedPlatform],
    queryFn: () => apiRequest("GET", `/api/social-inbox/accounts?platform=${selectedPlatform}`),
    enabled: !!selectedPlatform,
  });

  const accounts = accountsData?.accounts ?? [];

  // Build query params
  const queryParams = new URLSearchParams();
  if (selectedPlatform) queryParams.set("platform", selectedPlatform);
  if (selectedAccount) queryParams.set("accountId", selectedAccount.id);
  if (selectedType) queryParams.set("type", selectedType);
  const queryString = queryParams.toString();

  // Fetch messages — only when account is selected
  const {
    data: inboxData,
    isLoading,
    refetch,
  } = useQuery<InboxData>({
    queryKey: ["/api/social-inbox/messages", queryString],
    queryFn: () => apiRequest("GET", `/api/social-inbox/messages?${queryString}`),
    enabled: !!selectedAccount && !!selectedPlatform,
    refetchInterval: 60_000,
  });

  // Auto-select platform on first load
  useEffect(() => {
    if (platformInitialized || connectedPlatforms.length === 0) return;
    const connected = connectedPlatforms.filter((p) => p.connected);
    if (connected.length === 0) return;

    const saved = loadSavedState();
    const target =
      saved.platform && connected.find((p) => p.platform === saved.platform)
        ? saved.platform
        : connected[0].platform;

    setSelectedPlatform(target);
    setPlatformInitialized(true);
  }, [connectedPlatforms, platformInitialized]);

  // Auto-select account when accounts load for the current platform
  useEffect(() => {
    if (!accounts.length) return;
    const saved = loadSavedState();
    const savedAccountId = selectedPlatform
      ? saved.accountsByPlatform[selectedPlatform]
      : undefined;
    const match = savedAccountId ? accounts.find((a) => a.id === savedAccountId) : null;
    const toSelect = match ?? accounts[0];

    // Only update if the current selection is invalid or absent
    if (!selectedAccount || !accounts.find((a) => a.id === selectedAccount.id)) {
      setSelectedAccount(toSelect);
    }
  }, [accounts, selectedPlatform]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist selection to localStorage
  useEffect(() => {
    if (selectedPlatform && selectedAccount) {
      saveState(selectedPlatform, selectedAccount.id);
    }
  }, [selectedPlatform, selectedAccount]);

  const handlePlatformChange = useCallback(
    (platform: SocialPlatform) => {
      if (platform === selectedPlatform) return;
      setSelectedPlatform(platform);
      setSelectedAccount(null);
      setSelectedType(null);
    },
    [selectedPlatform]
  );

  const handleAccountChange = useCallback((account: AccountInfo) => {
    setSelectedAccount(account);
    setSelectedType(null);
  }, []);

  // Reply mutation
  const replyMutation = useMutation({
    mutationFn: (args: { commentId: string; platform: SocialPlatform; message: string }) => {
      const params = new URLSearchParams({ platform: args.platform });
      if (selectedAccount) params.set("accountId", selectedAccount.id);
      return apiRequest("POST", `/api/social-inbox/reply/${args.commentId}?${params}`, {
        message: args.message,
      });
    },
    onSuccess: () => {
      toast({ title: t("toast.replySent") });
      queryClient.invalidateQueries({ queryKey: ["/api/social-inbox/messages"] });
    },
    onError: (err: Error) => {
      toast({ title: t("toast.error"), description: t("toast.errorDesc"), variant: "destructive" });
    },
  });

  // Send DM mutation
  const dmMutation = useMutation({
    mutationFn: (args: { recipientId: string; platform: SocialPlatform; message: string }) =>
      apiRequest("POST", `/api/social-inbox/dm/send?platform=${args.platform}`, {
        recipientId: args.recipientId,
        message: args.message,
      }),
    onSuccess: () => {
      toast({ title: t("toast.messageSent") });
      queryClient.invalidateQueries({ queryKey: ["/api/social-inbox/messages"] });
    },
    onError: (err: Error) => {
      toast({ title: t("toast.error"), description: t("toast.errorDesc"), variant: "destructive" });
    },
  });

  const handleReply = useCallback(
    (commentId: string, platform: SocialPlatform, text: string) => {
      replyMutation.mutate({ commentId, platform, message: text });
    },
    [replyMutation]
  );

  const handleSendDm = useCallback(
    (recipientId: string, platform: SocialPlatform, text: string) => {
      dmMutation.mutate({ recipientId, platform, message: text });
    },
    [dmMutation]
  );

  const comments = inboxData?.comments ?? [];
  const conversations = inboxData?.conversations ?? [];
  const hasNoConnected = !connectedPlatforms.some((p) => p.connected);
  const hasData = comments.length > 0 || conversations.length > 0;
  const showComments = !selectedType || selectedType === "comment";
  const showDMs = !selectedType || selectedType === "dm";

  // ── No platforms connected ──
  if (hasNoConnected && platformsData) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-white/30">
        <Inbox size={48} className="mb-1" />
        <p className="text-sm font-medium text-white/50">{t("noPlatforms.heading")}</p>
        <p className="text-xs">{t("noPlatforms.subtext")}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setLocation("/integracje?context=organic")}
          className="mt-1 border-white/[0.12] text-white/60 hover:bg-white/[0.06]"
        >
          {t("noPlatforms.openIntegrations")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Left: Platform nav ── */}
      <div className="w-44 shrink-0 border-r border-white/[0.06] flex flex-col overflow-y-auto py-2">
        <p className="px-3 pt-1 pb-2 text-[10px] font-semibold text-white/20 uppercase tracking-wider">
          {t("platformsSection")}
        </p>
        {connectedPlatforms
          .filter((p) => p.connected)
          .map(({ platform }) => {
            const cfg = PLATFORM_CONFIG[platform];
            const isActive = selectedPlatform === platform;
            return (
              <button
                key={platform}
                onClick={() => handlePlatformChange(platform)}
                className={`flex items-center gap-2.5 px-3 py-2.5 text-xs font-medium transition-all ${
                  isActive
                    ? "bg-white/[0.08] text-white"
                    : "text-white/35 hover:text-white/65 hover:bg-white/[0.04]"
                }`}
              >
                <span className={isActive ? cfg.textColor : "text-white/25"}>{cfg.icon}</span>
                {cfg.label}
              </button>
            );
          })}
      </div>

      {/* ── Right: Content ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar: account + type filters + refresh */}
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <AccountDropdown
              accounts={accounts}
              selectedAccount={selectedAccount}
              accountsLoading={accountsLoading}
              onSelect={handleAccountChange}
            />

            {selectedAccount && (
              <>
                <div className="w-px h-4 bg-white/[0.08]" />
                <button
                  onClick={() => setSelectedType(null)}
                  className={`px-2.5 py-1.5 text-[11px] font-medium rounded-lg transition-all ${
                    !selectedType
                      ? "bg-white/[0.10] text-white"
                      : "text-white/30 hover:text-white/60 hover:bg-white/[0.04]"
                  }`}
                >
                  {t("filterAll")}
                </button>
                {(Object.keys(TYPE_CONFIG) as MessageType[]).map((msgType) => (
                  <button
                    key={msgType}
                    onClick={() => setSelectedType(selectedType === msgType ? null : msgType)}
                    className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium rounded-lg transition-all ${
                      selectedType === msgType
                        ? "bg-white/[0.10] text-white"
                        : "text-white/30 hover:text-white/60 hover:bg-white/[0.04]"
                    }`}
                  >
                    {TYPE_CONFIG[msgType].icon}
                    {t(`types.${msgType}`)}
                  </button>
                ))}
              </>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {comments.length + conversations.length > 0 && (
              <span className="px-2 py-0.5 text-[10px] font-medium bg-white/[0.06] text-white/40 rounded-full">
                {comments.length + conversations.length}
              </span>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => refetch()}
              disabled={isLoading}
              className="h-7 px-2 text-xs text-white/40 hover:text-white/70"
            >
              <RefreshCw size={12} className={isLoading ? "animate-spin" : ""} />
              {t("refresh")}
            </Button>
          </div>
        </div>

        {/* Message feed */}
        <div className="flex-1 overflow-y-auto">
          {/* No platform selected yet (before auto-select fires) */}
          {!selectedPlatform && !platformsData && (
            <div className="flex items-center justify-center py-20 text-white/20">
              <RefreshCw size={18} className="animate-spin mr-2" />
              <span className="text-sm">{t("loadingPlatforms")}</span>
            </div>
          )}

          {/* Account loading */}
          {selectedPlatform && !selectedAccount && accountsLoading && (
            <div className="flex items-center justify-center py-20 text-white/20">
              <RefreshCw size={18} className="animate-spin mr-2" />
              <span className="text-sm">{t("loadingAccounts")}</span>
            </div>
          )}

          {/* No accounts for platform */}
          {selectedPlatform && !selectedAccount && !accountsLoading && accounts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-white/25">
              <User size={40} className="mb-1" />
              <p className="text-sm font-medium text-white/40">{t("noAccounts.heading")}</p>
              <p className="text-xs">{t("noAccounts.subtext")}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLocation("/integracje?context=organic")}
                className="mt-1 border-white/[0.12] text-white/60 hover:bg-white/[0.06]"
              >
                {t("noPlatforms.openIntegrations")}
              </Button>
            </div>
          )}

          {/* Messages loading */}
          {selectedAccount && isLoading && (
            <div className="flex items-center justify-center py-20 text-white/20">
              <RefreshCw size={18} className="animate-spin mr-2" />
              <span className="text-sm">{t("loadingMessages")}</span>
            </div>
          )}

          {selectedAccount && !isLoading && !hasData && <EmptyState />}

          {selectedAccount && !isLoading && hasData && (
            <div className="p-4 space-y-6">
              {showComments && comments.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare size={14} className="text-white/40" />
                    <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                      {t("commentsSection", { count: comments.length })}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {comments.map((msg) => (
                      <CommentCard
                        key={msg.id}
                        message={msg}
                        onReply={handleReply}
                        isReplying={replyMutation.isPending}
                      />
                    ))}
                  </div>
                </div>
              )}

              {showDMs && conversations.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Mail size={14} className="text-white/40" />
                    <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                      {t("dmsSection", { count: conversations.length })}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {conversations.map((conv) => (
                      <ConversationCard
                        key={conv.id}
                        conversation={conv}
                        onSendDm={handleSendDm}
                        isSending={dmMutation.isPending}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
